import { FC, useMemo, useState } from "react";
import {
	Box,
	Button,
	IconButton,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddIcon from "@mui/icons-material/Add";
import ScienceIcon from "@mui/icons-material/Science";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import { useSearchParams } from "react-router-dom";

import ConfigDeleteDialog, {
	type DeleteDependency,
} from "../../components/ConfigListShell/ConfigDeleteDialog";
import { ConfigListViewControls } from "../../components/ConfigListShell/ConfigListToolbar";
import { type ViewMode } from "../../components/ConfigListShell/useConfigList";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { AnalyteStatus, IAnalyte } from "../../domain/analyte.interface";
import { CupResponse } from "../../domain/cup.interface";
import { useAnalytes, useDeleteAnalyte } from "../../hooks/useAnalytes";
import {
	useCupDependencies,
	useCups,
	useDeleteCup,
	useDuplicateCup,
} from "../../hooks/useCups";
import { useUserStore } from "../../stores/user.store";
import AuditLogModal from "../../components/modals/AuditLog";
import {
	analyteDeleteEntry,
	CUPS_AUDIT_MODULE,
	cupDeleteEntry,
	cupDuplicateEntry,
	useCupsAudit,
} from "./cupsAudit";
import { duplicateCreatedNotice } from "./cupDuplicate";
import {
	cupDeleteAllowedBody,
	cupDeleteBlockedSuggestion,
	cupDeleteDependencies,
	cupDeleteName,
	dependenciesFromConflict,
} from "./cupDelete";
import {
	analyteDeleteAllowedBody,
	analyteDeleteBlockedSuggestion,
	analyteDeleteDependencies,
	analyteDeleteName,
	cupsFromConflict,
} from "./analyteDelete";
import { cupsCarrying } from "./analytesList";
import AnalytesPanel from "./AnalytesPanel";
import CupEditor from "./CupEditor";
import AnalyteEditor from "./AnalyteEditor";
import CupsPanel from "./CupsPanel";
import { vendorFilterOptions } from "./cupsList";
import { searchForTab, tabFromSearch, type MergedTab } from "./tabs";

/**
 * SIMS-8783 — Cups and analytes on one screen.
 *
 * Imports here are RELATIVE on purpose. The `@/*` alias in tsconfig is honoured by tsc and by
 * jest's moduleNameMapper, but NOT by CRA's webpack — react-scripts reads `baseUrl` and ignores
 * `paths`. Every `@/` import that works elsewhere in src/ resolves to a TYPE, which Babel erases
 * before webpack ever sees it (e.g. cup.service.ts:10 uses CupStatus only as an annotation).
 * Importing a runtime value through the alias compiles, type-checks and unit-tests green, then
 * fails to resolve the moment the dev server builds it.
 *
 * Three structural decisions, each recorded in construction/functional-design.md:
 *
 *  - The active tab lives in the URL, not in state. The Hi-Fi keeps it in state and loses it on
 *    reload; C17944 requires it to survive one.
 *  - `view` is held HERE and passed down, because the design shares one view across both tabs
 *    (markup 1002). Everything else — search, filter, sort, page — belongs to the panel, so
 *    switching tabs unmounts it and clears ALL of them. The design clears only the QUERY on a tab
 *    switch and deliberately leaves the filter set (second-reader D6, which records that asymmetry
 *    as a defect risk). Clearing everything is the stronger behaviour here, because the two tabs
 *    filter on different dimensions and a surviving vendor filter would be meaningless on
 *    analytes — but it IS a deviation, and it is the shape question Q7 asks about.
 *  - The header count states the WHOLE catalogue and never follows a filter. The per-tab result
 *    count is the one AC3 means (second-reader diff D2).
 */
// SIMS-9251 — tab labels are labels, not buttons: no uppercase (markup L117-123, 14px / 600).
// Set on each Tab rather than through Tabs, so it does not depend on selector specificity.
const TAB_SX = {
	textTransform: "none",
	fontSize: "0.875rem",
	fontWeight: 600,
} as const;

const CupsAndAnalytes: FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = tabFromSearch(`?${searchParams.toString()}`);

	const [view, setView] = useState<ViewMode>("table");

	// SIMS-8799 — the editor lives in component state on this screen, not on a route of its own.
	// That is what the design models (`s.page === 'cup'`, breadcrumb back to the list) and what
	// Foundation's worked example does (Payers/index.tsx:92,649).
	const [editing, setEditing] = useState<{ cup?: CupResponse } | null>(null);

	// SIMS-8814 — the analyte editor, held the same way the cup editor is: component state
	// on this screen, not a route of its own.
	const [editingAnalyte, setEditingAnalyte] = useState<{
		analyte?: IAnalyte;
	} | null>(null);

	// SIMS-8850 — the confirmation is the container's, not the panel's, so the cup editor can
	// reach the same dialog when SIMS-8799 wires its header Delete.
	const [deleting, setDeleting] = useState<CupResponse | null>(null);
	// Counts the SERVER refused with, kept when a delete loses the race against a visit being
	// recorded. They outrank the fetched ones: they are the reason the delete was refused.
	const [conflictCounts, setConflictCounts] = useState<
		DeleteDependency[] | null
	>(null);
	const deleteCup = useDeleteCup();
	const { showSnackBar } = useFeedBack();
	const recordAudit = useCupsAudit();
	const [auditOpen, setAuditOpen] = useState(false);

	// SIMS-8853 — the analyte confirmation, held here for the same reason as the cup one: the list
	// and the analyte editor's header both open it.
	const [deletingAnalyte, setDeletingAnalyte] = useState<IAnalyte | null>(null);
	const [analyteConflict, setAnalyteConflict] = useState<
		DeleteDependency[] | null
	>(null);
	const deleteAnalyte = useDeleteAnalyte();

	// SIMS-8806 — an analyte being created from the open cup. While set, the analyte form shows
	// and the cup editor stays MOUNTED but hidden, so its draft, baseline and dirty state survive
	// untouched — including for a cup that has never been saved.
	const [holdingCup, setHoldingCup] = useState<{ name: string } | null>(null);
	const [addedAnalyte, setAddedAnalyte] = useState<{
		id: string;
		code: string;
		added: boolean;
	} | null>(null);
	// The created analyte, until the refetched catalogue carries it, so its chip renders at once.
	const [createdAnalyte, setCreatedAnalyte] = useState<IAnalyte | null>(null);

	const analyteCreatedFromCup = (analyte: IAnalyte): void => {
		setHoldingCup(null);
		setCreatedAnalyte(analyte);
		// FR-43 (SIMS-8839): an inactive analyte cannot be added to a cup.
		const added = analyte.status === AnalyteStatus.ACTIVE;
		setAddedAnalyte({ id: analyte._id, code: analyte.shortname, added });
		showSnackBar(
			added
				? `“${analyte.shortname}” created and added to this cup. Your cup edits are unchanged.`
				: `“${analyte.shortname}” was created as Inactive, so it was not added to this cup.`,
			added ? "success" : "warning",
		);
	};

	const closeCupEditor = (): void => {
		setEditing(null);
		setHoldingCup(null);
		setAddedAnalyte(null);
		setCreatedAnalyte(null);
	};

	// SIMS-8826 — duplicate from the list or from the editor. The copy is created at once; the user
	// lands on the list, where the name sort puts it directly below the original.
	const duplicate = useDuplicateCup();
	const { userInfo } = useUserStore();
	const duplicateCup = async (cup: CupResponse): Promise<void> => {
		if (duplicate.isPending) {
			return;
		}
		try {
			const copy = await duplicate.mutateAsync({
				id: cup._id,
				createdBy: userInfo?.id || "SYSTEM_USER",
			});
			await recordAudit([cupDuplicateEntry(cup, copy.name)]);
			setEditing(null);
			showSnackBar(duplicateCreatedNotice(copy.name), "success");
		} catch {
			showSnackBar("The cup could not be duplicated.", "error");
		}
	};

	const {
		data: cupsData,
		isLoading: cupsLoading,
		isError: cupsError,
	} = useCups();
	const {
		data: analytesData,
		isLoading: analytesLoading,
		isError: analytesError,
	} = useAnalytes();

	const cups = useMemo<CupResponse[]>(() => cupsData?.body ?? [], [cupsData]);
	const analytes = useMemo<IAnalyte[]>(
		() => analytesData?.body ?? [],
		[analytesData],
	);
	const vendors = useMemo(() => vendorFilterOptions(cups), [cups]);
	const cupEditorAnalytes = useMemo(
		() =>
			createdAnalyte && !analytes.some((a) => a._id === createdAnalyte._id)
				? [...analytes, createdAnalyte]
				: analytes,
		[analytes, createdAnalyte],
	);

	const cupsSettled = !cupsLoading && !cupsError;
	const analytesSettled = !analytesLoading && !analytesError;

	const selectTab = (next: MergedTab) => {
		const search = searchForTab(next);
		setSearchParams(search ? new URLSearchParams(search) : {}, {
			replace: false,
		});
	};

	// Fetched only while the confirmation is open: the counts are needed for ONE cup at the moment
	// deletion is asked for, not for every row on every list load.
	const {
		data: dependencyCounts,
		isFetching: isCheckingDependencies,
		isError: dependenciesUnavailable,
	} = useCupDependencies(deleting?._id ?? null, Boolean(deleting));

	const deleteDependencies = useMemo(
		() => conflictCounts ?? cupDeleteDependencies(dependencyCounts),
		[conflictCounts, dependencyCounts],
	);

	const askDelete = (cup: CupResponse): void => {
		setConflictCounts(null);
		setDeleting(cup);
	};

	const closeDelete = (): void => {
		setDeleting(null);
		setConflictCounts(null);
	};

	const confirmDelete = async (): Promise<void> => {
		if (!deleting) {
			return;
		}
		try {
			await deleteCup.mutateAsync(deleting._id);
			await recordAudit([cupDeleteEntry(deleting)]);
			showSnackBar("Cup deleted successfully", "success");
			closeDelete();
		} catch (error) {
			// AC2/AC3/AC4 from the SERVER. The dialog's counts were read when it opened; the guard
			// re-counts at delete time, so a cup that became used in between is refused here and
			// the dialog turns blocked with the server's numbers rather than closing on a failure.
			const refused = dependenciesFromConflict(error);
			if (refused) {
				setConflictCounts(refused);
				return;
			}
			showSnackBar("The cup could not be deleted.", "error");
		}
	};

	// SIMS-8853 AC1 — the count is derived from the cups the screen already holds, the same source
	// as the "In cups" column, so the dialog and the row can never disagree. Inactive cups count.
	// While the cups query is unsettled the count is unknown, and the dialog withholds Delete.
	const analyteDependencies = useMemo(
		() =>
			analyteConflict ??
			(deletingAnalyte && cupsSettled
				? analyteDeleteDependencies(cupsCarrying(deletingAnalyte, cups))
				: []),
		[analyteConflict, deletingAnalyte, cupsSettled, cups],
	);

	const askDeleteAnalyte = (analyte: IAnalyte): void => {
		setAnalyteConflict(null);
		setDeletingAnalyte(analyte);
	};

	const closeDeleteAnalyte = (): void => {
		setDeletingAnalyte(null);
		setAnalyteConflict(null);
	};

	const confirmDeleteAnalyte = async (): Promise<void> => {
		if (!deletingAnalyte) {
			return;
		}
		const name = analyteDeleteName(deletingAnalyte);
		try {
			await deleteAnalyte.mutateAsync(deletingAnalyte._id);
			await recordAudit([analyteDeleteEntry(deletingAnalyte)]);
			showSnackBar(`${name} deleted`, "success");
			closeDeleteAnalyte();
			// Deleted from its own editor: there is nothing left to edit.
			setEditingAnalyte(null);
		} catch (error) {
			// The server re-counts at delete time; a cup that took the analyte on in between is
			// refused here and the dialog turns blocked with the server's count.
			const refused = cupsFromConflict(error);
			if (refused) {
				setAnalyteConflict(refused);
				return;
			}
			showSnackBar("The analyte could not be deleted.", "error");
		}
	};

	const analyteDeleteDialog = deletingAnalyte && (
		<ConfigDeleteDialog
			open
			recordName={analyteDeleteName(deletingAnalyte)}
			recordNoun="analyte"
			dependencies={analyteDependencies}
			isCheckingDependencies={cupsLoading && !analyteConflict}
			dependenciesUnavailable={cupsError && !analyteConflict}
			dependencyNoun="cups"
			blockedSuggestion={analyteDeleteBlockedSuggestion}
			allowedBody={analyteDeleteAllowedBody(deletingAnalyte)}
			isDeleting={deleteAnalyte.isPending}
			onConfirm={confirmDeleteAnalyte}
			onClose={closeDeleteAnalyte}
		/>
	);

	if (editingAnalyte) {
		return (
			<>
				<AnalyteEditor
					analyte={editingAnalyte.analyte}
					cups={cups}
					// AC3's usage list is derived from the cups collection, so an unsettled cups
					// query means the blast radius of a rename is UNKNOWN, not zero.
					cupsUnavailable={!cupsSettled}
					onExit={() => setEditingAnalyte(null)}
					onDelete={askDeleteAnalyte}
				/>
				{analyteDeleteDialog}
			</>
		);
	}

	if (editing) {
		// The cup editor is always rendered in the same place, so holding a cup only hides it.
		return (
			<>
				<Box sx={{ display: holdingCup ? "none" : "block" }}>
					<CupEditor
						cup={editing.cup}
						analytes={cupEditorAnalytes}
						// The editor must be able to tell "no analytes to pick" from "the catalogue has not
						// arrived". Without these an unsettled query renders an empty checklist, and the
						// user can save a cup with nothing on it believing there was nothing to choose.
						analytesLoading={analytesLoading}
						analytesError={analytesError}
						onExit={closeCupEditor}
						onDuplicate={duplicateCup}
						onNewAnalyte={(name) => {
							setAddedAnalyte(null);
							setHoldingCup({ name });
						}}
						addedAnalyte={addedAnalyte}
					/>
				</Box>
				{holdingCup && (
					<AnalyteEditor
						cups={cups}
						cupsUnavailable={!cupsSettled}
						heldCupName={holdingCup.name}
						onCreated={analyteCreatedFromCup}
						// Every way out of the form returns to the held cup (AC3).
						onExit={() => setHoldingCup(null)}
					/>
				)}
			</>
		);
	}

	return (
		<Box sx={{ p: 3, display: "flex", flexDirection: "column", minHeight: 0 }}>
			<Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
				<Box>
					<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
						Program setup
					</Typography>
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", lineHeight: "2rem", fontWeight: 600 }}
					>
						Cups &amp; analytes
					</Typography>
					{/* The whole catalogue. Deliberately NOT the match count — see D2. A count is
					    withheld until its query settles: stating "0 cups" while the skeleton is on
					    screen, or after a failed load, is an absence of data dressed as a fact. */}
					<Typography
						sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
						data-testid="cups-analytes-total"
					>
						{`${cupsSettled ? cups.length : "—"} cups · ${
							analytesSettled ? analytes.length : "—"
						} analytes · ${cupsSettled ? vendors.length : "—"} ${
							vendors.length === 1 && cupsSettled ? "vendor" : "vendors"
						}`}
					</Typography>
				</Box>
				<Box sx={{ flex: 1 }} />
				{/* SIMS-9251 — the design orders the header view toggle → audit → Create (markup L96-115).
				    Rows-per-page is NOT here: the markup puts it in each list's footer (D8). */}
				<ConfigListViewControls
					view={view}
					onViewChange={setView}
					pageSize="10"
					onPageSizeChange={() => undefined}
					pageSizeOptions={[]}
					showPageSize={false}
				/>
				{/* SIMS-8861 — the Foundation audit-log button (Actions/Payers), before Create as in
				    the design (markup L105-107). */}
				<Tooltip title="Audit log">
					<IconButton
						aria-label="Audit log"
						onClick={() => setAuditOpen(true)}
						sx={{
							width: "2.5rem",
							height: "2.5rem",
							borderRadius: "0.5rem",
							border: "1px solid",
							borderColor: "primary.main",
							color: "primary.main",
							backgroundColor: "background.paper",
							"&:hover": {
								backgroundColor: (t) => alpha(t.palette.primary.main, 0.06),
							},
						}}
					>
						<HistoryOutlinedIcon sx={{ fontSize: "1.25rem" }} />
					</IconButton>
				</Tooltip>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() =>
						tab === "cups" ? setEditing({}) : setEditingAnalyte({})
					}
				>
					{tab === "cups" ? "Create cup" : "Create analyte"}
				</Button>
			</Box>

			<Tabs
				value={tab}
				onChange={(_, next: MergedTab) => selectTab(next)}
				sx={{
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Tab
					value="cups"
					icon={<WaterDropIcon fontSize="small" />}
					iconPosition="start"
					sx={TAB_SX}
					label={`Cups ${cupsSettled ? cups.length : "—"}`}
				/>
				<Tab
					value="analytes"
					icon={<ScienceIcon fontSize="small" />}
					iconPosition="start"
					sx={TAB_SX}
					label={`Analytes ${analytesSettled ? analytes.length : "—"}`}
				/>
			</Tabs>

			{tab === "cups" ? (
				<CupsPanel
					cups={cups}
					analytes={analytes}
					onEdit={(cup) => setEditing({ cup })}
					onDelete={askDelete}
					onDuplicate={duplicateCup}
					isLoading={cupsLoading}
					isError={cupsError}
					view={view}
				/>
			) : (
				<AnalytesPanel
					analytes={analytes}
					cups={cups}
					cupsUnavailable={!cupsSettled}
					onEdit={(analyte) => setEditingAnalyte({ analyte })}
					onDelete={askDeleteAnalyte}
					isLoading={analytesLoading}
					isError={analytesError}
					view={view}
				/>
			)}

			{/* SIMS-8850. The shared dialog already carries the four states this story needs —
			    checking, unavailable, blocked, allowed — and withholds Delete for all but the
			    last. The blocked state has no Delete button at all, which is UI, not the guard:
			    DELETE /cups/:id re-counts server-side and refuses on its own (PR #1353). */}
			{deleting && (
				<ConfigDeleteDialog
					open
					recordName={cupDeleteName(deleting)}
					recordNoun="cup"
					dependencies={deleteDependencies}
					// Once the server has refused, its answer stands. Without the guard a window
					// refocus refetches (staleTime: 0) and flips the refusal back to "Checking…".
					isCheckingDependencies={isCheckingDependencies && !conflictCounts}
					dependenciesUnavailable={dependenciesUnavailable}
					dependencyNoun="treatment plans and recorded visits"
					blockedSuggestion={cupDeleteBlockedSuggestion}
					allowedBody={cupDeleteAllowedBody(deleting)}
					isDeleting={deleteCup.isPending}
					onConfirm={confirmDelete}
					onClose={closeDelete}
				/>
			)}
			{analyteDeleteDialog}
			{/* Mounted only while open: the modal runs its read on mount. */}
			{auditOpen ? (
				<AuditLogModal
					open
					module={CUPS_AUDIT_MODULE}
					title="Audit log — Cups & analytes"
					onClose={() => setAuditOpen(false)}
				/>
			) : null}
		</Box>
	);
};

export default CupsAndAnalytes;
